import React from "react";
import axios from "axios";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
    Button,
    Col,
    Row,
    Spinner,
    Alert,
    Form as BootstrapForm,
} from "react-bootstrap";
import { toast } from "sonner";
import "./ContactForm.css"; // 🟢 Uses custom CSS here
import './Header.css'
import { isValidUSPhone } from '../../utils/phoneValidation';
import { validateOhioAddress } from '../../utils/addressValidation';

const contactFormValidationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: Yup.string()
        .required("Phone Number is required"),
    address: Yup.string().required("Address is required"),
    service: Yup.string().required("Service is required"),
    message: Yup.string().required("Message is required"),
});

export default function ContactForm({ source = "modal", handleClose }) {
    const getApiUrl = (path) => {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const base = isLocal ? 'http://localhost:8081' : 'https://ultimatesroofing.com/api';
        return `${base}${path}`;
    };

    const url = getApiUrl("/ultimates/customer/register");

    const handleSubmit = async (
        values,
        { setStatus, resetForm, setSubmitting, setFieldError }
    ) => {
        setStatus(null);

        if (!isValidUSPhone(values.phoneNumber)) {
            toast.error("Please enter a valid US phone number");
            setSubmitting(false);
            return;
        }

        const addrResult = await validateOhioAddress(values.address);
        if (!addrResult.valid) {
            toast.error(addrResult.error);
            setFieldError('address', addrResult.error);
            setSubmitting(false);
            return;
        }

        try {
            // Split full name into first name and last name
            const nameParts = values.name.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const { name, ...otherValues } = values;

            const formData = new FormData();
            formData.append(
                "customerJson",
                JSON.stringify({
                    ...otherValues,
                    firstName,
                    lastName,
                    source,
                })
            );

            await axios.post(url, formData);

            resetForm();
            handleClose();
            const successMsg = source === "request"
                ? "We will call you back shortly."
                : "We will get back to you as soon as possible.";
            toast.success(successMsg);
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Failed to submit form. Please try again.";
            toast.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={{
                name: "",
                email: "",
                phoneNumber: "",
                address: "",
                service: "",
                message: "",
            }}
            validationSchema={contactFormValidationSchema}
            onSubmit={handleSubmit} className="fontLight"
        >
            {({ isSubmitting, status, touched, errors }) => (
                <Form className="quote-form-formik main ">
                    {status?.error && (
                        <Alert variant="danger" className="mb-3">
                            {status.error}
                        </Alert>
                    )}

                    <Row className="mb-3">
                        <Col md={12}>
                            <BootstrapForm.Group controlId="name">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Name <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="name"
                                    as={BootstrapForm.Control}
                                    placeholder="Type Your Full Name"
                                    className="fontLight"
                                    isInvalid={touched.name && !!errors.name}
                                />

                            </BootstrapForm.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <BootstrapForm.Group controlId="email">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Email <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="email"
                                    type="email"
                                    as={BootstrapForm.Control}
                                    placeholder="Type Your Email Address"
                                    className="fontLight"
                                    isInvalid={touched.email && !!errors.email}
                                />

                            </BootstrapForm.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <BootstrapForm.Group controlId="phoneNumber">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Phone Number <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="phoneNumber"
                                    as={BootstrapForm.Control}
                                    placeholder="e.g. +1614-357-7539"
                                    className="fontLight"
                                    isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                                />

                            </BootstrapForm.Group>
                        </Col>
                        <Col md={6}>
                            <BootstrapForm.Group controlId="address">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Address <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="address"
                                    as={BootstrapForm.Control}
                                    placeholder="Property Address"
                                    className="fontLight"
                                    isInvalid={touched.address && !!errors.address}
                                />

                            </BootstrapForm.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col>
                            <BootstrapForm.Group controlId="service">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Service <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="service"
                                    as="select"
                                    className={`form-control fontLight ${touched.service && !!errors.service ? 'is-invalid' : ''}`}
                                >
                                    <option value="" disabled>Select Service</option>
                                    <option value="Residential Roofing">Residential Roofing</option>
                                    <option value="Commercial Roofing">Commercial Roofing</option>
                                    <option value="Siding Service">Siding Service</option>
                                    <option value="Gutters System">Gutters System</option>
                                    <option value="Window Service">Window Service</option>
                                    <option value="Painting Service">Painting Service</option>
                                    <option value="Flooring Service">Flooring Service</option>
                                    <option value="Power Wash">Power Wash</option>
                                    <option value="Renovation Service">Renovation Service</option>
                                </Field>

                            </BootstrapForm.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col>
                            <BootstrapForm.Group controlId="message">
                                <BootstrapForm.Label className="fontHauora text-start justify-content-start d-flex">
                                    Message <span className="text-danger">*</span>
                                </BootstrapForm.Label>
                                <Field
                                    name="message"
                                    as="textarea"
                                    className={`form-control fontLight ${touched.message && !!errors.message ? 'is-invalid' : ''}`}
                                    rows={3}
                                    placeholder="Please share detailed information about your project to help us understand better."
                                />

                            </BootstrapForm.Group>
                        </Col>
                    </Row>

                    <div className="text-center mt-4">
                        <Button
                            type="submit"
                            className="submit-btn-custom custom-btn fontHauora"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{" "}
                                    Submitting…
                                </>
                            ) : (
                                "Submit"
                            )}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}
